import {
  FileUploader,
  LoadingIndicator,
  MUITextField,
} from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useRef, useState } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { FileVersionPost } from "../../../../models/FileVersion"
import { ControllerRendererProps } from "../../../../types/reactHookForm"

// Arcadia completes its internal upload animation in ten 500 ms steps.
const FILE_UPLOADER_PROCESSING_MS = 5100

const validationSchema = z.object({
  suffix: z
    .string()
    .regex(/^[a-zA-Z0-9_-]*$/, {
      message: "Only letters, numbers, underscores (_) and hyphens (-) are allowed",
    })
    .optional(),
  file: z.any().optional(),
})

export function FileVersionCreate() {
  const { fileId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/files/${fileId}#versions`
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [isUploadClicked, setIsUploadClicked] = useState(false)
  const [isFileReady, setIsFileReady] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const fileReadyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveRequestInFlightRef = useRef(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<FileVersionPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      suffix: "",
      file: null,
    })
  }, [reset])

  useEffect(
    () => () => {
      if (fileReadyTimeoutRef.current) {
        clearTimeout(fileReadyTimeoutRef.current)
      }
    },
    []
  )

  const onSubmit = async (values: FileVersionPost) => {
    const formData = new FormData()
    formData.append("suffix", values.suffix ?? "")
    if (values.file instanceof File) {
      formData.append("formFile", values.file)
    } else {
      toast.error("File is required")
      return
    }

    if (saveRequestInFlightRef.current) {
      return
    }

    saveRequestInFlightRef.current = true
    setIsSaving(true)
    try {
      const response = await digitalManagerApi.post(
        `/files/${fileId}/versions`,
        formData,
        {
        headers: { "Content-type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && progressEvent.total > 0) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            )
            setUploadProgress(progress)
          } else {
            setUploadProgress(0)
          }
        },
        }
      )

      if (response.status === StatusCodes.CREATED) {
        toast.success("File created")
        navigate(returnURL)
      }
    } catch {
      toast.error("Cannot create file")
    } finally {
      setUploadProgress(0)
      setIsSaving(false)
      saveRequestInFlightRef.current = false
    }
  }

  const selectedFile = useWatch({
    control,
    name: "file",
  })

  const handleSuffixChange = useCallback(
    (field: ControllerRenderProps<FileVersionPost, "suffix">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderSuffixTextField = useCallback(
    ({ field }: ControllerRendererProps<FileVersionPost, "suffix">) => (
      <MUITextField
        id="suffix"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleSuffixChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.suffix?.message === "string"
            ? { suffix: { message: errors.suffix.message } }
            : undefined
        }
      />
    ),
    [errors.suffix, handleSuffixChange]
  )

  const handleFileUpload = useCallback(
    (files: File[]) => {
      if (fileReadyTimeoutRef.current) {
        clearTimeout(fileReadyTimeoutRef.current)
      }

      setIsFileReady(false)
      setValue("file", files[0] ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      })

      if (files[0]) {
        fileReadyTimeoutRef.current = setTimeout(() => {
          setIsFileReady(true)
          fileReadyTimeoutRef.current = null
        }, FILE_UPLOADER_PROCESSING_MS)
      }
    },
    [setValue]
  )

  const handleFileDelete = useCallback(() => {
    if (fileReadyTimeoutRef.current) {
      clearTimeout(fileReadyTimeoutRef.current)
      fileReadyTimeoutRef.current = null
    }
    setIsFileReady(false)
    setValue("file", null, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }, [setValue])

  const submitForm = handleSubmit(async (values) => {
    setIsUploadClicked(true)
    setUploadProgress(0)
    await onSubmit(values)
  })

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={submitForm}
      isSubmitting={isSubmitting || isSaving}
      isDirty={isDirty && !!selectedFile}
      isValid={isValid && !!selectedFile && isFileReady}
    >
      <FieldRow label="Suffix">
        <Controller name="suffix" control={control} render={renderSuffixTextField} />
      </FieldRow>
      <FieldRow label="File">
        <FileUploader
          variant="single"
          id="file"
          disabled={isSubmitting || isSaving}
          onFileUpload={handleFileUpload}
          onFileDelete={handleFileDelete}
          errorMessages={{
            uploadSuccess: "File selected",
          }}
        />
      </FieldRow>
      {isUploadClicked && (
        <LoadingIndicator
          variant="progress-bar"
          size="medium"
          isDeterminate
          percentage={uploadProgress}
        />
      )}
    </FormPage>
  )
}
